import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Tooltip,} from '@mui/material';
import {Edit, Lock, Straighten, TextFields} from '@mui/icons-material';
import {FabricObject} from 'fabric';
import {OctagonShapeIcon} from '../shared/OctagonShapeIcon';
import {CircleShapeIcon} from '../shared/CircleShapeIcon';
import {EllipseShapeIcon} from '../shared/EllipseShapeIcon';
import {TriangleShapeIcon} from '../shared/TriangleShapeIcon';
import {HexagonShapeIcon} from '../shared/HexagonShapeIcon';
import {RectangleIcon} from '../shared/RectangleIcon';
import {getTableCapacity} from '../utils/fabric-factory';
import {pulseHighlight, TABLE_CAPACITY_MAX} from '../utils/constants';
import FloorPlanInlineNumberInput from './FloorPlanInlineNumberInput';
import {PreviewTableIcon} from './PreviewTableIcon';

export const TABLE_LIST_ITEM_COLUMNS = {
    capacity: 72,
    action: 56,
    gap: 6,
    actionSlot: 24,
};

const SHAPE_ICON_MAP: Record<string, React.FC<{ fontSize?: 'small' | 'inherit' | 'medium' | 'large' }>> = {
    circle: CircleShapeIcon,
    ellipse: EllipseShapeIcon,
    triangle: TriangleShapeIcon,
    hexagon: HexagonShapeIcon,
    octagon: OctagonShapeIcon,
};

interface ElementListItemProps {
    element: FabricObject;
    isSelected: boolean;
    isHighlighted: boolean;
    onSelect: (element: FabricObject, shiftKey: boolean) => void;
    onActivate?: (element: FabricObject) => void;
    onUnlock?: (element: FabricObject) => void;
    onEdit: (elementId: string, element: FabricObject) => void;
    onCapacityChange?: (element: FabricObject, capacity: number) => void;
}

export const ElementListItem: React.FC<ElementListItemProps> = ({
                                                                    element,
                                                                    isSelected,
                                                                    isHighlighted,
                                                                    onSelect,
                                                                    onActivate,
                                                                    onUnlock,
                                                                    onEdit,
                                                                    onCapacityChange,
                                                                }) => {
    const {t} = useTranslation();
    const item = element.floorPlanItem;
    const id = item?.id;
    const isSelectable = element.selectable !== false;
    const table = item?.table;
    const elementData = item?.element;
    const kind = elementData?.kind;
    const shape = table?.shape ?? (kind === 'shape' ? elementData?.shape : undefined);
    const canvas = elementData?.canvas as any;
    const label = canvas?.label;

    const capacity = table ? getTableCapacity(table.seats, table.resource?.capacity ?? 0) : undefined;

    const handleCapacityCommit = useCallback((nextCapacity: number | undefined) => {
        if (nextCapacity == null || nextCapacity === capacity) return;
        onCapacityChange?.(element, nextCapacity);
    }, [capacity, element, onCapacityChange]);

    const handleCapacityActivate = useCallback(() => {
        onActivate?.(element);
    }, [element, onActivate]);

    const tableName = table ? (table.resource?.name ?? t('Table')) : '';
    const lockTooltipProps = {
        title: t('Locked (not selectable)'),
        placement: 'top' as const,
        disableInteractive: true,
        slotProps: {popper: {modifiers: [{name: 'offset', options: {offset: [0, -8]}}]}},
    };
    const editTooltipProps = {
        title: t('Edit'),
        placement: 'top' as const,
        disableInteractive: true,
        slotProps: {popper: {modifiers: [{name: 'offset', options: {offset: [0, -8]}}]}},
    };

    const displayName = () => {
        if (table) return tableName;
        if (kind === "text") return canvas?.text ?? t('Text');
        if (kind === "wall") return label || t('Wall');
        return label || (shape ? shape.charAt(0).toUpperCase() + shape.slice(1) : t('Rect'));
    };

    const lockSlot = (
        <Stack
            alignItems="center"
            justifyContent="center"
            sx={{width: TABLE_LIST_ITEM_COLUMNS.actionSlot}}
        >
            {!isSelectable && (
                <Tooltip {...lockTooltipProps}>
                    <IconButton
                        size="small"
                        sx={{p: 0.5}}
                        onClick={(e) => {
                            e.stopPropagation();
                            onUnlock?.(element);
                        }}
                    >
                        <Lock fontSize="small" sx={{color: 'text.disabled'}}/>
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );

    const editSlot = (
        <Stack
            alignItems="center"
            justifyContent="center"
            sx={{width: TABLE_LIST_ITEM_COLUMNS.actionSlot}}
        >
            <Tooltip {...editTooltipProps}>
                <IconButton size="small" sx={{p: 0.5}} onClick={(e) => {
                    e.stopPropagation();
                    onEdit(id!, element);
                }}>
                    <Edit fontSize="small"/>
                </IconButton>
            </Tooltip>
        </Stack>
    );

    return (
        <ListItem
            disablePadding
            secondaryAction={
                table ? (
                    <Stack
                        direction="row"
                        spacing={`${TABLE_LIST_ITEM_COLUMNS.gap}px`}
                        alignItems="center"
                    >
                        <Stack
                            sx={{
                                width: TABLE_LIST_ITEM_COLUMNS.capacity,
                                alignItems: 'center',
                            }}
                        >
                            {capacity != null && (
                                <FloorPlanInlineNumberInput
                                    value={capacity}
                                    onCommit={handleCapacityCommit}
                                    min={0}
                                    max={TABLE_CAPACITY_MAX}
                                    onActivate={handleCapacityActivate}
                                    stopPropagation
                                    sx={{width: 44}}
                                />
                            )}
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            justifyContent="flex-end"
                            sx={{width: TABLE_LIST_ITEM_COLUMNS.action}}
                        >
                            {lockSlot}
                            {editSlot}
                        </Stack>
                    </Stack>
                ) : (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{width: TABLE_LIST_ITEM_COLUMNS.action}}
                    >
                        {lockSlot}
                        {editSlot}
                    </Stack>
                )
            }
            sx={{
                ...(isHighlighted ? {
                    animation: `${pulseHighlight} 1500ms ease-out`,
                } : undefined),
            }}
        >
            <ListItemButton
                selected={isSelected}
                onClick={(e) => onSelect(element, e.shiftKey)}
                sx={{pr: 8, minHeight: 44}}
            >
                <ListItemIcon sx={{minWidth: 36}}>
                    <ElementIcon
                        item={item}
                        table={table}
                        kind={kind}
                        shape={shape}
                        canvas={canvas}
                    />
                </ListItemIcon>
                <ListItemText
                    primary={displayName()}
                    primaryTypographyProps={{
                        variant: 'body2',
                        fontSize: '0.875rem',
                        noWrap: true
                    }}
                />
            </ListItemButton>
        </ListItem>
    );
};

const ElementIcon: React.FC<{
    item: any;
    table: any;
    kind: string | undefined;
    shape: string | undefined;
    canvas: any;
}> = ({item, table, kind, shape, canvas}) => {
    if (table && item) {
        return <PreviewTableIcon item={item}/>;
    }
    if (kind === "text") {
        return <TextFields fontSize="small"/>;
    }
    if (kind === "wall") {
        const points = canvas?.points as { x: number; y: number }[] | undefined;
        const isVertical = points && points.length >= 2 &&
            Math.abs(points[points.length - 1].y - points[0].y) >
            Math.abs(points[points.length - 1].x - points[0].x);
        return <Straighten fontSize="small"
                           sx={isVertical ? {transform: 'rotate(90deg)'} : undefined}/>;
    }
    if (kind === "shape") {
        const ShapeIcon = SHAPE_ICON_MAP[shape ?? ''];
        if (ShapeIcon) return <ShapeIcon fontSize="small"/>;
        return <RectangleIcon fontSize="small"/>;
    }
    return null;
};
